import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import api from '../../utils/api';
import { ListPageLayout, EmptyState } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function FinanceIncomes() {
  const [incomes, setIncomes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [mobileMoneyDialog, setMobileMoneyDialog] = useState(false);
  const [mobileMoneyForm, setMobileMoneyForm] = useState({ name: '', amount: '', purpose: '' });
  const [mobileMoneySubmitting, setMobileMoneySubmitting] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchIncomes();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchIncomes();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/finance/income-categories');
      setCategories(response.data.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchIncomes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/finance/incomes', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setIncomes(response.data.data || []);
      setTotal(Number(response.data.pagination?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch incomes');
      console.error('Error fetching incomes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRecordMobileMoney = async () => {
    const duesCategory = categories.find((c) => c.name === 'Dues (Mobile Money)');
    if (!duesCategory) {
      setError('Income category "Dues (Mobile Money)" not found. Please add it in Income Categories first.');
      return;
    }
    if (!mobileMoneyForm.amount || parseFloat(mobileMoneyForm.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    try {
      setMobileMoneySubmitting(true);
      setError('');
      await api.post('/finance/incomes', {
        name: mobileMoneyForm.name || `Mobile Money - ${new Date().toLocaleDateString()}`,
        category_id: duesCategory.id,
        amount: parseFloat(mobileMoneyForm.amount),
        purpose: mobileMoneyForm.purpose || 'Dues paid via mobile money',
        created_at: new Date().toISOString().split('T')[0],
      });
      setMobileMoneyDialog(false);
      setMobileMoneyForm({ name: '', amount: '', purpose: '' });
      fetchIncomes();
      queryClient.invalidateQueries('dashboardMetrics');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record mobile money payment');
    } finally {
      setMobileMoneySubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this income?')) {
      return;
    }
    try {
      await api.delete(`/finance/incomes/${id}`);
      fetchIncomes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete income');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get('/finance/incomes/export', {
        params: { format, search: search || undefined },
        responseType: 'blob',
      });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incomes.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export incomes');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || '-';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ListPageLayout
      title="Income"
      crumbs={
        <>
          Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Income</Box>
        </>
      }
      searchPlaceholder="Search incomes..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      actions={
        <>
          <Button
            variant="contained"
            color="success"
            startIcon={<PhoneAndroidIcon />}
            onClick={() => setMobileMoneyDialog(true)}
          >
            Record Mobile Money
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/income/new')}
          >
            Add Income
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => handleExport('excel')}
            size="small"
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => handleExport('csv')}
            size="small"
          >
            CSV
          </Button>
        </>
      }
    >
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incomes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                      <EmptyState
                        title="No income records yet"
                        description="Record your first income or add a mobile money payment to get started."
                        actionLabel="Add Income"
                        onAction={() => navigate('/dashboard/income/new')}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  incomes.map((income) => (
                    <TableRow key={income.id} hover>
                      <TableCell>{income.name}</TableCell>
                      <TableCell>
                        <Chip label={getCategoryName(income.category_id)} size="small" />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(income.amount)}</TableCell>
                      <TableCell>{formatDate(income.created_at)}</TableCell>
                      <TableCell>{income.purpose || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/income/${income.id}`)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(income.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      <Dialog open={mobileMoneyDialog} onClose={() => !mobileMoneySubmitting && setMobileMoneyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Mobile Money Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Money received via mobile money will be added to income and reflected on the dashboard.
          </Typography>
          <TextField
            fullWidth
            label="Payer / Reference (optional)"
            value={mobileMoneyForm.name}
            onChange={(e) => setMobileMoneyForm({ ...mobileMoneyForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Amount (₵)"
            type="number"
            required
            inputProps={{ step: '0.01', min: 0 }}
            value={mobileMoneyForm.amount}
            onChange={(e) => setMobileMoneyForm({ ...mobileMoneyForm, amount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Purpose (optional)"
            value={mobileMoneyForm.purpose}
            onChange={(e) => setMobileMoneyForm({ ...mobileMoneyForm, purpose: e.target.value })}
            placeholder="e.g. Dues paid via mobile money"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileMoneyDialog(false)} disabled={mobileMoneySubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleRecordMobileMoney} disabled={mobileMoneySubmitting || !mobileMoneyForm.amount}>
            {mobileMoneySubmitting ? 'Saving...' : 'Add to Income'}
          </Button>
        </DialogActions>
      </Dialog>
    </ListPageLayout>
  );
}
